import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { getFormatDate } from 'core/classes/helpers/DateHelper'
import { getAvailableProgramTypes } from 'partner360/helpers/offerings/getAvailableProgramTypes'
import { getOfferingsInfo } from 'partner360/helpers/offerings/getOfferingsInfo'

import { getAcknowledgedOfferingData } from '../../lib/getAcknowledgedOfferingData'
import { isCockpitPlanningStarted } from '../../lib/getCockpitPlanningStarted'
import { getOfferingPlanningTone, TOfferingPlanningTone } from '../../lib/getOfferingPlanningTone'
import {
  selectCockpitAttentionCountByOffering,
  selectCockpitAttentionSeverityByOffering,
  selectCockpitCombinedPlanQualityByOffering,
  selectCockpitCompletenessData,
  selectCockpitFirstPartnerOfferingsCatalog,
  selectCockpitPartnerCompleteness,
  selectCockpitPlanningStarted,
} from '../selectors'

interface ICount {
  count: number
  total: number
}

export interface ICockpitPlanningStatus {
  /** A programme's share as a whole percent, `null` when none was measured; `Overall` counts the
   *  complete programmes. */
  completeness: { percent: number | null } | ICount
  /** A programme's signature, with its date when there is one, `null` when the payload says nothing
   *  about it; `Overall` counts the signed programmes. */
  acknowledgment: { acknowledged: boolean | null; date: string | null } | ICount
  score: number | null
  tone?: TOfferingPlanningTone
  /** How many attention statuses the tab lists. */
  attentionCount: number
}

export interface ICockpitPlanningStatuses {
  overall: ICockpitPlanningStatus
  byOffering: Map<string, ICockpitPlanningStatus>
  /** The most severe attention level per offering, for a tab the catalog does not list. */
  severities: ReadonlyMap<string, number>
}

/* Truncated rather than rounded, as the completeness detail does: 99.8% is not a finished plan. */
const toPercent = (value: number) => Math.floor(value * 100)

const isSameValue = (a: string, b: string) => a.trim() === b.trim()

/** `Overall` reads every offering's statuses, as the attention widget does on it, so it takes the
 *  most severe of all of them; with none, green once anything is planned. */
const getOverallTone = (severities: ReadonlyMap<string, number>, isPlanningStarted: boolean) =>
  getOfferingPlanningTone(
    severities.size ? Math.max(...severities.values()) : undefined,
    isPlanningStarted
  )

/**
 * The planning state of every enabled programme of the partner, and of `Overall` across them: how
 * complete the plan is, whether the PBM has signed it, its combined plan quality and the tone the
 * programme is marked with — the colour of its most severe attention status. One source for the
 * status line, the tab folds and the tab tooltips.
 */
export const useCockpitPlanningStatuses = (partnerId: string): ICockpitPlanningStatuses => {
  const offeringsCatalog = useSelector(selectCockpitFirstPartnerOfferingsCatalog)
  const completenessProfiles = useSelector(selectCockpitPartnerCompleteness)
  const completenessData = useSelector(selectCockpitCompletenessData)
  const planQuality = useSelector(selectCockpitCombinedPlanQualityByOffering)
  const attentionSeverities = useSelector(selectCockpitAttentionSeverityByOffering)
  const attentionCounts = useSelector(selectCockpitAttentionCountByOffering)
  const planningStarted = useSelector(selectCockpitPlanningStarted)

  return useMemo(() => {
    const { enabledOfferings } = getOfferingsInfo(getAvailableProgramTypes(offeringsCatalog))
    const byOffering = new Map<string, ICockpitPlanningStatus>()

    enabledOfferings.forEach(({ programType }) => {
      const completenessTotal = completenessProfiles.find(
        ({ profile }) => profile === programType
      )?.total
      const acknowledged = getAcknowledgedOfferingData(completenessData, partnerId, programType)
      const hasAcknowledgmentRecord = (completenessData ?? []).some(
        (item) => isSameValue(item.PartnerId, partnerId) && isSameValue(item.Offering, programType)
      )
      const score = planQuality.byOffering.get(programType) ?? null

      byOffering.set(programType, {
        completeness: { percent: completenessTotal == null ? null : toPercent(completenessTotal) },
        acknowledgment: {
          acknowledged: hasAcknowledgmentRecord ? Boolean(acknowledged) : null,
          date: acknowledged?.AcknowledgedDate
            ? (getFormatDate(acknowledged.AcknowledgedDate, 'date3') ?? null)
            : null,
        },
        score,
        tone: getOfferingPlanningTone(
          attentionSeverities.get(programType),
          isCockpitPlanningStarted(planningStarted, programType)
        ),
        attentionCount: attentionCounts.byOffering.get(programType) ?? 0,
      })
    })

    const statuses = [...byOffering.values()]
    const total = statuses.length

    return {
      overall: {
        completeness: {
          count: statuses.filter(
            ({ completeness }) => 'percent' in completeness && (completeness.percent ?? 0) >= 100
          ).length,
          total,
        },
        acknowledgment: {
          count: statuses.filter(
            ({ acknowledgment }) => 'acknowledged' in acknowledgment && acknowledgment.acknowledged
          ).length,
          total,
        },
        score: planQuality.overallScore,
        tone: getOverallTone(attentionSeverities, planningStarted.overall),
        attentionCount: attentionCounts.overall,
      },
      byOffering,
      severities: attentionSeverities,
    }
  }, [
    attentionCounts,
    attentionSeverities,
    completenessData,
    completenessProfiles,
    offeringsCatalog,
    partnerId,
    planQuality,
    planningStarted,
  ])
}

/** The status of the tab a key names: `Overall` or one enabled programme. A programme the catalog
 *  does not list still gets a line — every figure unknown, coloured by its attention statuses — so
 *  the band never goes blank. */
export const getCockpitPlanningStatus = (
  statuses: ICockpitPlanningStatuses,
  offering: string
): ICockpitPlanningStatus => {
  if (offering === ProgramTypes.Overall) {
    return statuses.overall
  }

  const severity = statuses.severities.get(offering)

  return (
    statuses.byOffering.get(offering) ?? {
      completeness: { percent: null },
      acknowledgment: { acknowledged: null, date: null },
      score: null,
      tone: severity === undefined ? undefined : getOfferingPlanningTone(severity, false),
      attentionCount: 0,
    }
  )
}

/** A plan is finished only when it is measured full and signed: `Overall` and the manager cockpit
 *  when every programme or partner they count is. Nothing raised on an unfinished plan is still not
 *  a clean bill, so the green check waits for this. */
export const isCockpitPlanFinished = ({
  completeness,
  acknowledgment,
}: Pick<ICockpitPlanningStatus, 'completeness' | 'acknowledgment'>) => {
  const isComplete =
    'percent' in completeness
      ? (completeness.percent ?? 0) >= 100
      : completeness.count >= completeness.total
  const isSigned =
    'acknowledged' in acknowledgment
      ? acknowledgment.acknowledged === true
      : acknowledgment.count >= acknowledgment.total

  return isComplete && isSigned
}
